<template>
  <div class="sidebar">
    <div class="shortcart">
      <h5>{{$t('shoppingCart.page.title')}}</h5>
        <ul class="products" v-if="room">
          <li class="suiteproduct">
            <div class="image--wrapper">
              <img :src="`${room.main_picture.absolute_path}?w=300&h=200&crop-to-fit`">
            </div>
            <div class="infoproduct">
              <h6>{{room.text.short_name}}</h6>
              <p v-if="sameDate" v-html="$t('step2.page.date', `<span>${roomStart}</span>`)"></p>
              <p v-else v-html="$t('step2.page.dates', `<span>${roomStart}</span>`, `<span>${roomEnd}</span>`)"></p>
              <p><strong>{{$t('shoppingCart.page.room.guests.' + (nbGuest > 1 ? 'plural': 'singular'), nbGuest)}}</strong></p>
              <div class="priceproduct">
                <template v-if="roomPrice !== altRoomPrice && roomPrice < altRoomPrice">
                  <span class="value-price stroke">
                    {{ altRoomPrice | decimalPrice }} €
                  </span>
                  <span class="alt-price">
                    {{ roomPrice | decimalPrice }} €
                  </span>
                </template>
                <template v-else>
                  <span class="value-price">
                    {{ roomPrice | decimalPrice }} €
                  </span>
                </template>
              </div>
            </div>
          </li>
          <template v-for="(extra, index) in extras" :index="index" :item="extra">
            <right-extra :extra="extra" @remove="handleRemove"/>
          </template>
        </ul>
        <div class="checkbox-options" v-if="optionsAvailable.length > 0">
          <ul>
            <li v-for="option in optionsAvailable" :key="option.id" @click="handleToggleOption(option.id)">
              <div class="checkbox-round" :class="{checked: hasOption(option.id)}"></div>
              <span>{{ template(option.text.checkbox, {$nbGuest: nbGuest, $plural: nbGuest > 1, $singular: nbGuest <= 1}) }}</span>
              <question-circle v-if="option.text.use_modal"></question-circle>
            </li>
          </ul>
        </div>
        <div class="taxesejour"><p>{{$t('shoppingCart.page.total.touristTax')}}</p><span>{{touristTax | decimalPrice}} €</span></div>
        <div class="taxesejour small" v-if="hasInsurance"><p>{{$t('shoppingCart.page.total.insurance')}}</p></div>
        <div class="taxesejour" v-if="extraPrice > 0"><p>{{$t('shoppingCart.page.total.extras')}}</p><span>{{extraPrice | decimalPrice}} €</span></div>
        <div class="taxesejour" v-if="extraReduction < 0"><p>{{$t('shoppingCart.page.total.reductions')}}</p><span>{{extraReduction | decimalPrice}} €</span></div>
        <div class="taxesejour" v-if="roomAdditional > 0"><p>Options</p><span>{{roomAdditional | decimalPrice}} €</span></div>
        <div class="total"><p>{{$t('shoppingCart.page.total.total')}}</p>
          <span v-if="altTotalBookingPrice === totalBookingPrice || altTotalBookingPrice < totalBookingPrice">{{totalBookingPrice | decimalPrice}} €</span>
          <span v-else>
            <span class="stroke">{{altTotalBookingPrice | decimalPrice}} €</span>
            {{totalBookingPrice | decimalPrice}} €
          </span>
        </div>
    </div>
    <div class="btn--component">
      <router-link tab="a" :to="{name: 'search'}" id="reserveother" class="btnshortcart">{{$t('shoppingCart.page.btn.bookOther')}}</router-link>
    </div>
    <div class="btn--component">
      <a href="javascript:void(0)" @click="handleDoneClick" id="finishorder" class="btnshortcart">{{$t('shoppingCart.page.btn.book')}}</a>
    </div>
    <resa-picker-standalone
        :default-start="bookingStart"
        :default-end="bookingEnd"
        :current-booking-room-id="bookingRoomId"
        :current-booking-id="bookingId" />
  </div>
</template>

<script>
  import {mapState} from "vuex";
  import moment from 'moment';
  import RightExtra from "./right-extra";
  import {templateParser} from '../../../plugins/template.plugin';
  import QuestionCircle from "../../../components/svg/question-circle";
  import ResaPicker from "../../rooms/components/resa-picker";
  import ResaPickerStandalone from "./resa-picker-standalone";

  function reducePriceplans(key, pricePlans, useAlt = false) {
    return pricePlans.reduce((result, value) => {
      return result + value.input_prices.reduce((inputValues, inputPrice) => {
        if (inputPrice.edges.length === 0) return inputValues;
        if (useAlt && inputPrice.edges[0].altPrice) return inputValues + inputPrice.edges[0].altPrice[key];
        return inputValues + inputPrice.edges[0][key];
      }, 0)
    }, 0)
  }

  export default {
    name: "right-recap",
    components: {ResaPickerStandalone, ResaPicker, QuestionCircle, RightExtra},
    computed: {
      totalBookingPrice () {
        return this.roomTotal + this.extraPrice + this.extraReduction;
      },
      altTotalBookingPrice() {
        return this.altRoomTotal + this.extraPrice + this.extraReduction
      },
      altRoomPrice() {
        return reducePriceplans('hostingPrice', this.pricePlans, true);
      },
      touristTax() {
        return reducePriceplans('touristTax', this.pricePlans, false);
      },
      altTouristTax() {
        return reducePriceplans('touristTax', this.pricePlans, true);
      },
      altRoomTotal() {
        return reducePriceplans('totalPrice', this.pricePlans, true);
      },
      roomTotal() {
        return reducePriceplans('totalPrice', this.pricePlans, false);
      },
      altRoomAdditional() {
        return reducePriceplans('additionalPrice', this.pricePlans, true)
            + reducePriceplans('optionsAdditionalPrice', this.pricePlans, true)
            + reducePriceplans('optionsBasePrice', this.pricePlans, true);
      },
      roomAdditional() {
        return reducePriceplans('additionalPrice', this.pricePlans, false)
            + reducePriceplans('optionsAdditionalPrice', this.pricePlans, false)
            + reducePriceplans('optionsBasePrice', this.pricePlans, false);
      },
      roomPrice() {
        return reducePriceplans('hostingPrice', this.pricePlans, false);
      },
      ...mapState({
        hasRoom: s => s.room.room !== null,
        selectedOptions: s => s.shoppingCart.booking.bookingRoom.price_options,
        bookingId: s => s.shoppingCart.booking.bookingRoom.bookingRoom.booking.id,
        bookingRoomId: s => s.shoppingCart.booking.bookingRoom.bookingRoom.id,
        bookingStart: s => s.shoppingCart.booking.bookingRoom.bookingRoom.booking.start,
        bookingEnd: s => s.shoppingCart.booking.bookingRoom.bookingRoom.booking.end,
        optionsAvailable: s => s.shoppingCart.booking.bookingRoom.bookingRoom.booking.room_price.price_options,
        pricePlans: s => s.shoppingCart.booking.bookingRoom.bookingRoom.price_plans,
        roomPriceData: s => s.shoppingCart.booking.bookingRoom.bookingRoom.booking.room_price,
        room: s => s.shoppingCart.booking.bookingRoom.bookingRoom.room,
        roomStart: s => moment(s.shoppingCart.booking.bookingRoom.bookingRoom.booking.start).format('ddd DD MMM'),
        roomEnd: s => moment(s.shoppingCart.booking.bookingRoom.bookingRoom.booking.end).format('ddd DD MMM'),
        sameDate: s => moment(s.shoppingCart.booking.bookingRoom.bookingRoom.booking.end).format('YYYYMMDD') === moment(s.shoppingCart.booking.bookingRoom.bookingRoom.booking.start).format('YYYYMMDD'),
        nbGuest: s => s.shoppingCart.booking.bookingRoom.bookingRoom.nb_guests,
        hasInsurance: s => s.shoppingCart.booking.bookingRoom.bookingRoom.booking.room_price.has_insurance,
        extraPrice: s => s.shoppingCart.booking.bookingRoom.bookingRoom.extras.reduce((prev, extra) => {
          // if (extra.is_option) return prev;
          return prev + (extra.extra.price.amount * extra.dates.reduce((p, n) => {
            return p + n.count;
          }, 0))
        }, 0),
        extraReduction: s => s.shoppingCart.booking.bookingRoom.bookingRoom.extras.reduce((prev, extra) => {
          if (!extra.extra.discount) {
            return prev;
          }
          if (extra.extra.discount.type === 'FIX') {
            return prev - extra.extra.discount.amount.amount;
          }
          const extraPrice = (extra.extra.price.amount * extra.dates.reduce((p, n) => {
            return p + n.count;
          }, 0));
          return prev - (extraPrice * extra.extra.discount.amount.amount) / 100;
        }, 0),
        extras: s => s.shoppingCart.booking.bookingRoom.bookingRoom.extras
      })
    },
    methods: {
      moment,
      template: templateParser,
      handleDoneClick() {
        this.$emit('done');
      },
      handleRemove(data) {
        this.$store.commit('shoppingCart/booking/bookingRoom/REMOVE_EXTRA', data);
      },
      async handleToggleOption(optionId) {
        await this.$store.dispatch('shoppingCart/booking/bookingRoom/toggleOption', optionId);
      },
      hasOption(optionId) {
        return this.selectedOptions.indexOf(optionId) !== -1;
      }
    }

  }
</script>
<style scoped>
  .infoadditional {
    width: 100%!important;
  }
  .text-grey {
    font-family: 'Sofia Pro Extra', sans-serif;
    color: var(--black50);
    font-size: .8em;
    margin: 0;
  }
  .small {
    font-size: .8rem;
  }
</style>



// WEBPACK FOOTER //
// src/pages/tunnel/step3/right-recap.vue